/**
 * usePresence — online-presence read-side hook.
 *
 * Connection-count presence (no heartbeat column). Online = the user
 * has ≥1 active WebSocket connection in this tenant. The BE computes
 * that from the `next-{stage}-websocket-connections` DDB table's
 * `tenantId-index` GSI; this hook is the FE view of that snapshot.
 *
 * Lifecycle:
 *   1. Mount → TanStack Query GET `/api/me/presence` once. `staleTime:
 *      Infinity` because the WS keeps the cache live — there is no
 *      auto-refetch and no polling.
 *   2. The AppShell-scope `useGlobalRealtime` listens for
 *      `presence.online` / `presence.offline` frames and mutates the
 *      cached query data via `queryClient.setQueryData(PRESENCE_QUERY_KEY)`.
 *   3. Consumers (chat sidebar DM row, DM header) call `isOnline(userId)`.
 *
 * Self-receive (a `presence.online` frame whose payload `userId`
 * matches the current user) is harmless — the cached Set dedupes.
 * The WS connection itself IS the user's online signal, so the
 * snapshot may already include them on first render.
 *
 * Empty-payload-safe: while the bootstrap query is in flight or
 * errored, `onlineUserIds` is `[]` and `isOnline` always returns
 * false. Treating "unknown" as "offline" matches the legacy
 * fallback — we never lie that someone is online.
 */

import { useQuery, type useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { apiClient } from '@/api/client'

export const PRESENCE_QUERY_KEY = ['me', 'presence'] as const

export interface PresenceSnapshot {
  onlineUserIds: string[]
}

export interface UsePresenceResult {
  isOnline: (userId: string) => boolean
  onlineUserIds: string[]
}

/**
 * Read the cached presence snapshot WITHOUT subscribing to query state.
 * Used by `useGlobalRealtime` so the WS-event handler can mutate the
 * cache without coupling AppShell re-renders to presence shape.
 */
export function getPresenceSnapshot(
  queryClient: ReturnType<typeof useQueryClient>,
): PresenceSnapshot {
  return queryClient.getQueryData<PresenceSnapshot>(PRESENCE_QUERY_KEY) ?? { onlineUserIds: [] }
}

/**
 * Apply a `presence.online` event to the cached snapshot — idempotent
 * (Set semantics dedupe). Used by `useGlobalRealtime`.
 */
export function applyPresenceOnline(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
): void {
  queryClient.setQueryData<PresenceSnapshot>(PRESENCE_QUERY_KEY, (prev) => {
    const ids = prev?.onlineUserIds ?? []
    if (ids.includes(userId)) return prev ?? { onlineUserIds: ids }
    return { onlineUserIds: [...ids, userId] }
  })
}

/**
 * Apply a `presence.offline` event to the cached snapshot. No-op if
 * the userId wasn't in the set.
 */
export function applyPresenceOffline(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
): void {
  queryClient.setQueryData<PresenceSnapshot>(PRESENCE_QUERY_KEY, (prev) => {
    const ids = prev?.onlineUserIds ?? []
    if (!ids.includes(userId)) return prev ?? { onlineUserIds: ids }
    return { onlineUserIds: ids.filter((id) => id !== userId) }
  })
}

export function usePresence(): UsePresenceResult {
  const query = useQuery<PresenceSnapshot>({
    queryKey: PRESENCE_QUERY_KEY,
    queryFn: async (): Promise<PresenceSnapshot> => {
      // BE response shape per the plan: `{ data: { onlineUserIds } }`
      // (Phase 2b uniform envelope). Unwrap here so the cache holds the
      // snapshot directly.
      const res = await apiClient<{ data: PresenceSnapshot }>('/api/me/presence')
      return { onlineUserIds: res.data?.onlineUserIds ?? [] }
    },
    // WS is the source of truth post-bootstrap. The query never
    // auto-refetches; cache mutations driven by `useGlobalRealtime`
    // keep the snapshot live.
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  })

  const onlineUserIds = useMemo(
    () => query.data?.onlineUserIds ?? [],
    // `query.data` identity is stable across renders unless the cache
    // is mutated by `setQueryData`, which is exactly the trigger we
    // want for downstream memos.
    [query.data],
  )

  // Memoize the lookup Set so consumers calling `isOnline` for many
  // userIds (e.g. a sidebar with N DM rows) don't pay O(N²).
  const onlineSet = useMemo(() => new Set(onlineUserIds), [onlineUserIds])

  const isOnline = useCallback((userId: string) => onlineSet.has(userId), [onlineSet])

  return { isOnline, onlineUserIds }
}
